import { useCallback, useEffect, useRef, useState } from 'react'
import styled, { css } from 'styled-components'
import { BreakpointWidthPx } from '../../../../css/BreakpointWidthPx'
import { blinkKeyframes } from '../../MenuBlinkAnimation'
import { useAppDispatch, useAppSelector } from '../../../../store/hooks'
import { getMenuState, setMenuTreeAllView } from '../../../../store/menuSlice'
import {
  openGvPanel,
  setGvSidePanelContent,
  setReadMoreNode,
} from '../../../../store/sidePanelSlice'
import {
  ScaniaAdobeEventId,
  ScaniaAdobeTrackingClickBmReadMoreEvent,
  ScaniaAdobeTrackingMenuClickEvent,
  pushAdobeEvent,
} from '../../../../utils/adobeAnalytics'
import { SvgChevronDown, SvgInfo } from '../../../../components/SvgImports'
import { GuidedViewMenuNode } from '../../../../api/generated'
import { getSessionInitDataState } from '../../../../store/sessionDataSlice'
import { useClient } from '../../../../utils/useClient'
import { SidePanelContent } from '../../../../store/types'
import { BreakpointHeightPx } from '../../../../css/BreakpointHeightPx'
import { TdsTooltip } from '@scania/tegel-react'
import {
  getUrlCidBevSemiliveParameters,
  UrlParamMode,
} from '../../../../utils/getUrlCidParameter'
import { TestElementTypeId } from '../../../../types/TestAttributeId'

const SubMenuRoot = styled.div``

interface SubMenuItemProps {
  $menuLevel: number
  $isActive: boolean
  $isHighlighted: boolean
  $hasSelectableItems: boolean
}

const SubMenuItemWrapper = styled.div<SubMenuItemProps>`
  --bg-color: ${(props) =>
    props.$isActive && props.$menuLevel === 3
      ? 'var(--tds-grey-100)'
      : 'var(--tds-white)'};
  background-color: var(--bg-color);
  color: ${({ $hasSelectableItems }) =>
    $hasSelectableItems ? 'var(--tds-grey-958)' : 'var(--tds-grey-800)'};
  display: flex;
  align-items: center;
  position: relative;
  height: ${(props) => {
    if (props.$menuLevel === 2) {
      return '68px'
    } else {
      return '48px'
    }
  }};

  ${(props) =>
    props.$menuLevel === 2 &&
    css`
      border-bottom: thin solid var(--tds-grey-300);
    `}

  ${(props) =>
    props.$menuLevel > 2 &&
    props.$isActive &&
    css`
      @media screen and (max-width: ${BreakpointWidthPx.Small}px),
        screen and (max-height: ${BreakpointHeightPx.Small}px) {
        border-bottom: thin solid var(--tds-blue-400);
      }
    `}

    @media (hover: hover) and (pointer: fine) {
    &:hover {
      background-color: var(--tds-grey-50);
      cursor: pointer;
    }
  }

  animation-duration: 500ms;
  animation-iteration-count: 3;
  animation-name: ${(props) =>
    props.$isHighlighted ? blinkKeyframes : 'unset'};
`

/*
const CurrentSelectionWrapper = styled.div`
  display: none;
  margin-left: 24px;
  border-right: thin solid var(--tds-blue-400);
  border-bottom: thin solid var(--tds-blue-400);
  border-left: thin solid var(--tds-blue-400);

  @media screen and (max-width: ${BreakpointWidthPx.Tablet}px) {
    display: block;
  }
`
*/

const BlueOpenIndicator = styled.div`
  min-width: 5px;
  height: 48px;
  float: left;
  background-color: var(--tds-blue-400);
`

const Level3Container = styled.div``

interface IconWrapperProps {
  $hasReadMore: boolean
}

const IconSpisaWrapper = styled.div<IconWrapperProps>`
  margin-right: 16px;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;

  visibility: hidden;

  ${({ $hasReadMore }) =>
    $hasReadMore &&
    css`
      cursor: pointer;
      visibility: visible;
    `};

  @media (hover: hover) and (pointer: fine) {
    &:hover {
      background-color: ${(props) =>
        props.$hasReadMore ? 'var(--tds-grey-50)' : ''};
    }
  }

  svg {
    margin-left: 6px;
    height: 20px;
    width: 20px;
  }
`

interface MenuItemProps {
  $menuLevel: number
  $hasSelectableItems: boolean
  $isOpen: boolean
}

const MenuItem = styled.div<MenuItemProps>`
  height: 100%;
  width: 100%;
  display: flex;
  align-items: center;
  position: relative;
  font-weight: ${(props) =>
    props.$isOpen || props.$menuLevel === 2 ? 'bold' : ''};
  color: ${(props) =>
    props.$hasSelectableItems ? 'inherit' : 'var(--tds-grey-600)'};
  padding-left: ${(props) => (props.$menuLevel > 2 ? '' : '16px')};
  text-overflow: ellipsis;
`

const MenuItemTextEllipsis = styled.p`
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
`

interface ChevronWrapperProps {
  $isOpen: boolean
  $menuLevel: number
}

const ChevronWrapper = styled.div<ChevronWrapperProps>`
  ${(props) =>
    props.$menuLevel > 2 &&
    css`
      visibility: hidden;

      @media screen and (max-width: ${BreakpointWidthPx.Small}px),
        screen and (max-height: ${BreakpointHeightPx.Small}px) {
        visibility: visible;
      }
    `}

  svg {
    height: 16px;
    width: 16px;
    margin-right: 24px;
    margin-left: 24px;

    transform: ${(props) => (props.$isOpen ? 'rotate(180deg)' : '')};
    transition-duration: 150ms;
    transition-property: transform;
  }
`

function shouldScrollIntoView(props: SubMenuProps) {
  if (props.node.id === props.highlightedId) {
    return true
  }
  if (props.menuLevel !== 3) {
    return false
  }
  if (!props.node.isOpen) {
    return false
  }
  if (!props.highlightedId) {
    return false
  }
  return true
}

export interface SubMenuProps {
  menuLevel: number
  node: GuidedViewMenuNode
  highlightedId: string | null
  closeCabColourMenuFirstTime: boolean
  setCloseCabColourMenuFirstTime: () => void
}

export const SubMenu = (props: SubMenuProps): JSX.Element => {
  const rootRef = useRef<HTMLDivElement | null>(null)
  const dispatch = useAppDispatch()
  const client = useClient()
  const sessionInitData = useAppSelector(getSessionInitDataState)
  const [arrayWithAllLevel2OpenStatus, setArrayWithAllLevel2OpenStatus] =
    useState<{ [id: string]: boolean }>({})
  const menuTree = useAppSelector(getMenuState).menuResultAllView?.menuTree
  const menuLevel2 = menuTree?.subMenus
    ? menuTree?.subMenus.find((menu) => menu.isOpen === true)?.subMenus
    : null
  const urlCidParams = getUrlCidBevSemiliveParameters(UrlParamMode.ExcludeBev)
  const showFpcCodeOnHover = urlCidParams.has('fpc')

  const handleMenuClick = (menuId: string, text: string) => {
    if (!sessionInitData?.sessionId) {
      return
    }
    if (!client) {
      return
    }
    if (!menuLevel2) {
      return
    }

    // A menu at level 2 should be able to collapse or open up, and still keep the menu below open to show the leafItems
    const wasOpenAtClick = arrayWithAllLevel2OpenStatus[menuId]
    if (wasOpenAtClick) {
      const id = menuId
      const isOpen = !wasOpenAtClick
      setArrayWithAllLevel2OpenStatus((prevValue) => {
        return { ...prevValue, [id]: isOpen }
      })
      return
    }

    const asyncWrapper = async () => {
      const newMenuState = await client.exploreMenu({
        sessionId: sessionInitData.sessionId,
        menuId: menuId,
        showAllConfigMenu: true,
      })
      if (newMenuState.success) {
        dispatch(setMenuTreeAllView(newMenuState.success))

        const trackingEvent: ScaniaAdobeTrackingMenuClickEvent = {
          event: ScaniaAdobeEventId.MenuClick,
          eventInfo: {
            optionClickedId: menuId,
            optionClickedText: text,
          },
        }
        pushAdobeEvent(trackingEvent)
      }
    }
    asyncWrapper()
    setArrayWithAllLevel2OpenStatus((prevValue) => {
      return { ...prevValue, [menuId]: true }
    })
    if (props.closeCabColourMenuFirstTime) {
      props.setCloseCabColourMenuFirstTime()
    }
  }

  // Scroll into view if "shouldScrollIntoView" is fulfilled
  useEffect(() => {
    if (!rootRef.current) {
      return
    }

    if (!shouldScrollIntoView(props)) {
      return
    }
    // This is a desperate quickfix, for some reason scrolling will not work
    // without the nested requestAnimationFrame calls.
    //
    // TODO: When there is time, try moving this scroll code out to
    // ConfiguratorMenu or BuildModePage?
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          if (!rootRef.current) {
            console.warn('Expected rootRef.current to be defined.')
            return
          }
          rootRef.current.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
          })
        })
      })
    })
  }, [props])

  const handleReadmoreClick = useCallback(
    async (
      e: React.MouseEvent<HTMLDivElement, MouseEvent>,
      node: GuidedViewMenuNode,
    ) => {
      e.stopPropagation()
      e.preventDefault()

      dispatch(setReadMoreNode({ id: node.id, title: node.shortText || null }))
      dispatch(openGvPanel())
      dispatch(setGvSidePanelContent(SidePanelContent.readMore))

      const trackingEvent: ScaniaAdobeTrackingClickBmReadMoreEvent = {
        event: ScaniaAdobeEventId.BmReadMoreClick,
      }
      pushAdobeEvent(trackingEvent)
    },
    [dispatch],
  )

  useEffect(() => {
    var level2OpenStatus: { [id: string]: boolean } = {}
    if (!menuLevel2) {
      return
    }
    menuLevel2.forEach((menuItem) => {
      const id = menuItem.id
      var isOpen =
        menuLevel2.find((item) => item.id === menuItem.id)?.isOpen || false

      // SC1M-3082: First time opening All options, the cab colour menu should be collapsed
      if (
        menuItem.subMenus &&
        menuItem.subMenus[0].shortText?.includes('Cab colour') &&
        props.closeCabColourMenuFirstTime
      ) {
        isOpen = false
      }
      level2OpenStatus[id] = isOpen
    })
    setArrayWithAllLevel2OpenStatus(level2OpenStatus)
  }, [menuLevel2, props.closeCabColourMenuFirstTime])

  return (
    <SubMenuRoot data-name="SubMenuRoot" ref={rootRef}>
      <SubMenuItemWrapper
        data-name="ItemWrapper"
        data-test-menu-id={props.node.id}
        $hasSelectableItems={props.node.hasSelectableItems}
        $isActive={props.node.isOpen}
        $isHighlighted={props.node.id === props.highlightedId}
        $menuLevel={props.menuLevel}
        onClick={() =>
          handleMenuClick(props.node.id, props.node.shortText || '')
        }
        data-test-element-type={
          props.menuLevel === 2
            ? TestElementTypeId.AllViewMenuLevel2
            : TestElementTypeId.AllViewMenuLevel3
        }
        data-test-etel-variant-id={props.node.id}
      >
        {props.menuLevel > 2 && (
          <Level3Container>
            <IconSpisaWrapper
              $hasReadMore={props.node.hasreadMore}
              onClick={(e) => handleReadmoreClick(e, props.node)}
            >
              <BlueOpenIndicator
                style={{ visibility: props.node.isOpen ? 'visible' : 'hidden' }}
              />
              <SvgInfo />
            </IconSpisaWrapper>
          </Level3Container>
        )}

        <MenuItem
          $menuLevel={props.menuLevel}
          $hasSelectableItems={props.node.hasSelectableItems}
          $isOpen={props.node.isOpen}
          id={'submenu' + props.node.id}
        >
          <MenuItemTextEllipsis>{props.node.shortText}</MenuItemTextEllipsis>
          {showFpcCodeOnHover && (
            <TdsTooltip
              placement="bottom"
              selector={'#submenu' + props.node.id}
              text={props.node.id}
            />
          )}
        </MenuItem>

        <ChevronWrapper
          data-name="Chevron"
          $isOpen={arrayWithAllLevel2OpenStatus[props.node.id]?.valueOf()}
          $menuLevel={props.menuLevel}
        >
          <SvgChevronDown />
        </ChevronWrapper>
      </SubMenuItemWrapper>
      {arrayWithAllLevel2OpenStatus[props.node.id]?.valueOf()
        ? props.node.subMenus?.map((menuItem) => {
            return (
              <SubMenu
                key={menuItem.id}
                menuLevel={props.menuLevel + 1}
                node={menuItem}
                highlightedId={props.highlightedId}
                closeCabColourMenuFirstTime={props.closeCabColourMenuFirstTime}
                setCloseCabColourMenuFirstTime={
                  props.setCloseCabColourMenuFirstTime
                }
              />
            )
          })
        : null}
    </SubMenuRoot>
  )
}
